import { TableWithPagination, Tooltip } from '@clickcease/cc-client-components-lib'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useAppStore } from 'Stores/appStoreModuleFed'
import { formatNumber } from 'Utils/number'

import { STATUSES } from '../../../../../hooks/statuses/statuses.model'
import { useGetBMProtectionStatus } from '../../../../../hooks/statuses/useGetBMProtectionStatus'
import { useBMStore } from '../../../../../stores/useBMStore'

import {
  TableStyles,
  WrapperStyles,
  StyledTr,
  StyledTd,
  StyledTh,
  StatusWrapper,
  TooltipWrapper,
  GetProtectedButton,
} from './DomainsTable.styled'
import { NormalizedSummaryData } from '../../../../../hooks/api/BMDataTypes'

interface DomainRowData {
  address: string
  totalValidVisits: number
  invalidTraffic: number
  knownCrawlers: number
  status: string
}

interface DomainsTableProps {
  summary: NormalizedSummaryData
}

export const DomainsTable: React.FC<DomainsTableProps> = ({ summary }) => {
  const { t } = useTranslation('botMitigation')
  const domains = useBMStore((state) => state.domains)
  const chosenDomains = useBMStore((state) => state.chosenDomains)
  const navigate = useNavigate()
  const { setCurrentDomain, setModalToShow } = useAppStore()
  const { summaryEntries } = summary

  const protectionStatus = useGetBMProtectionStatus(domains)

  const data = useMemo(() => {
    const domainsToProcess = chosenDomains && chosenDomains.length > 1 ? chosenDomains : domains

    return domainsToProcess.map((domain) => {
      const domainSummary = summaryEntries?.find((item) => item?.domain === domain.address) ?? {
        protectedValidVisits: 0,
        totalBlocked: 0,
        whiteListed: 0,
      }

      return {
        address: domain.address,
        totalValidVisits: domainSummary.protectedValidVisits,
        invalidTraffic: domainSummary.totalBlocked,
        knownCrawlers: domainSummary.whiteListed,
        status: t(
          `protectionStatus.${protectionStatus[domain.address] ?? 'wordpressNotRecognized'}`
        ),
      }
    })
  }, [domains, chosenDomains, summaryEntries, protectionStatus, t])

  const columnNames = [
    <StyledTh key="domain">{t('domainsTableColumnNames.domain')}</StyledTh>,
    <StyledTh key="totalValidVisits" centered>
      {t('domainsTableColumnNames.totalValidVisits')}
    </StyledTh>,
    <StyledTh key="invalidTraffic" centered>
      {t('domainsTableColumnNames.invalidTraffic')}
    </StyledTh>,
    <StyledTh key="knownCrawlers" centered>
      {t('domainsTableColumnNames.knownCrawlers')}
    </StyledTh>,
    <StyledTh key="getProtectedButtonColumn"></StyledTh>,
  ]

  const [currentPage, setCurrentPage] = useState(1)
  const paginationLimit = 5
  const totalCount = data.length
  const pageCount = Math.ceil(totalCount / paginationLimit)

  const paginatedData = data.slice(
    (currentPage - 1) * paginationLimit,
    currentPage * paginationLimit
  )

  const onClickHandler = (rowData: DomainRowData) => {
    const domainObj = domains.find((domain) => domain.address === rowData.address)

    if (protectionStatus[rowData.address] === STATUSES.notProtected.label && domainObj) {
      setCurrentDomain(domainObj)
      setModalToShow('bzSetup')
    } else if (
      protectionStatus[rowData.address] === STATUSES.wordpressNotRecognized.label &&
      domainObj
    ) {
      setCurrentDomain(domainObj)
      setModalToShow('editDomainProducts')
    } else {
      navigate('/domain-management', { replace: true })
    }
  }

  const body = paginatedData.map((row, index) => {
    const statusKey = protectionStatus[row.address] ?? 'wordpressNotRecognized'
    const statusInfo = STATUSES[statusKey]

    return (
      <StyledTr key={index}>
        <StyledTd>
          <TooltipWrapper>
            {statusInfo.icon && (
              <Tooltip tooltiptContent={t(`protectionStatus.${statusKey}`)}>
                <FontAwesomeIcon
                  icon={statusInfo.icon}
                  style={{ color: statusInfo.color, width: '20px', height: '20px' }}
                  size={'xs'}
                />
              </Tooltip>
            )}
            {row.address}
          </TooltipWrapper>
        </StyledTd>
        <StyledTd centered>{formatNumber(row.totalValidVisits)}</StyledTd>
        <StyledTd centered>{formatNumber(row.invalidTraffic)}</StyledTd>
        <StyledTd centered>{formatNumber(row.knownCrawlers)}</StyledTd>
        <StyledTd end>
          {statusKey && (
            <StatusWrapper>
              {statusKey !== STATUSES.protected.label && (
                <GetProtectedButton onClick={() => onClickHandler(row)} variant={'ghost'}>
                  <label>{t('getProtected')}</label>
                </GetProtectedButton>
              )}
            </StatusWrapper>
          )}
        </StyledTd>
      </StyledTr>
    )
  })

  return (
    <TableWithPagination
      columnsNames={columnNames}
      body={body}
      pageCount={pageCount}
      currentPage={currentPage}
      totalItems={totalCount}
      limitPerPage={paginationLimit}
      onFirstPage={() => setCurrentPage(1)}
      onLastPage={() => setCurrentPage(pageCount)}
      onNextPage={() => setCurrentPage((prev) => Math.min(prev + 1, pageCount))}
      onPreviousPage={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
      tableStyle={TableStyles}
      wrapperStyle={WrapperStyles}
    />
  )
}
